import React, { useState } from 'react';
import { Layers, Package, Play, History, Settings, Clock, BarChart3 } from 'lucide-react';
import BuildStackList from './BuildStackList.js';
import BuildStackPanel from './BuildStackPanel.js';
import CatalogPane from './CatalogPane.js';
import { useBuildStacksStore } from '../../stores/useBuildStacksStore.js';
import { formatDuration, estimateBuildDuration } from '../../utils/timeUtils.js';

interface BuildLabWorkspaceProps {
  className?: string;
}

export const BuildLabWorkspace: React.FC<BuildLabWorkspaceProps> = ({ className = '' }) => {
  const [activeTab, setActiveTab] = useState<'runs' | 'catalog'>('runs');
  const { activeStackId, stacks } = useBuildStacksStore();
  
  const totalBuilds = stacks.length;
  const avgDuration = stacks.length > 0 
    ? stacks.reduce((acc, stack) => acc + estimateBuildDuration(stack.steps?.length || 0), 0) / stacks.length
    : 0;

  const tabs = [
    {
      id: 'runs' as const,
      label: 'Runs',
      icon: Play,
      description: 'Build execution and workflow management'
    },
    {
      id: 'catalog' as const,
      label: 'Catalog',
      icon: Package,
      description: 'Templates, plugins, and marketplace'
    }
  ];

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Build Lab Header */}
      <div className="flex-shrink-0 p-6 border-b border-slate-700/50">
        <div className="flex items-center space-x-4 mb-4">
          <div className="relative">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 via-pink-500 to-blue-500 flex items-center justify-center shadow-lg">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-slate-900"></div>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Build Lab
            </h1>
            <p className="text-sm text-slate-400">Unified build orchestration &amp; marketplace</p>
          </div>
          
          {totalBuilds > 0 && (
            <div className="flex items-center space-x-4 text-sm text-slate-400">
              <div className="flex items-center space-x-1">
                <BarChart3 className="w-4 h-4" />
                <span>{totalBuilds} builds</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>~{formatDuration(avgDuration)} avg</span>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-slate-800/50 rounded-lg p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-orange-500/20 via-pink-500/20 to-blue-500/20 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
                title={tab.description}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'runs' ? (
          <div className="flex flex-1 h-full min-h-0 min-w-0 gap-4 p-4">
            <div className="w-80 flex-shrink-0">
              <BuildStackList />
            </div>
            <div className="flex flex-col flex-1 h-full min-h-0 min-w-0">
              <BuildStackPanel stackId={activeStackId || 'new'} />
            </div>
          </div>
        ) : (
          <div className="h-full p-4">
            <CatalogPane />
          </div>
        )}
      </div>
    </div>
  );
};
